<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    onBeforeUnmount,
    onMounted,
    watch,
  } from 'vue';
  import { StockRecipesService } from '@/api/services/stock-recipes.service';
  import ModalsStockItems from '@/components/modals/stock/ModalsStockItems.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import { useProductEditorContext } from '@/composition/catalog/use-product-editor';
  import { useStockLines } from '@/composition/catalog/use-stock-lines';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { savedSizes } from '@/domain/catalog/catalog-filters';
  import {
    hasStockRow,
    invalidStockRows,
    isEmptyStockUpdate,
    IStockRow,
    newStockRow,
    stockMassUpdate,
  } from '@/domain/catalog/stock-form';
  import { AlertType } from '@/interfaces/IAlert';
  import { IStockItem } from '@/interfaces/IStockItem';
  import V2CatalogEditorBanner from '../shared/V2CatalogEditorBanner.vue';

  /**
   * «Stock» of the product (plan §5.12): the supplies each unit uses in the
   * current branch, then those each size adds. Loaded when the row first
   * opens and saved after the product by the editor's «Guardar», through
   * the mass update of `ModalsItemStock.vue`. After an inventory change of
   * the assistant panel (plan P5-3), clean lines are read again; edited ones
   * that no longer match what is saved say so and block «Guardar» until
   * they are read again (`useStockLines`). `stale` tells the Stock row while
   * that waits.
   */
  interface IStockBlock {
    key: string;
    title: string | null;
    size: { optionId: number; optionItemId: number } | null;
    rows: IStockRow[];
  }

  const STOCK_ITEMS_MODAL = 'stock-items';

  const emit = defineEmits<{ (e: 'stale', value: boolean): void }>();

  const editor = useProductEditorContext();
  const { data } = useCatalogPage();
  const modals = useModal();
  const ui = useUi();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  const {
    loadedBranchId,
    base,
    rows,
    loadFailed,
    stale,
    dirty,
    busy,
    load,
    reload: reloadLines,
    beforeSave,
    reset,
  } = useStockLines({
    context: () => ({
      restaurantId: data.restaurant.value?.id ?? null,
      branchId: data.branchId.value,
      itemId: editor.itemId,
    }),
    saving: editor.saving,
    read: (target) =>
      StockRecipesService.findByItemId(
        target.restaurantId,
        target.branchId,
        target.itemId
      ),
  });

  let disposed = false;

  const branchName = computed(() => {
    if (data.activeStoreBranches.value.length < 2) return null;
    const id = loadedBranchId.value ?? data.branchId.value;
    return (
      data.storeBranches.value.find((branch) => branch.id === id)?.name ?? null
    );
  });

  const sizes = computed(() =>
    editor.item.value ? savedSizes(editor.item.value) : []
  );

  const blocks = computed<IStockBlock[]>(() => {
    const list: IStockBlock[] = [
      {
        key: 'base',
        title: sizes.value.length ? t('menuPage.stock.everySize') : null,
        size: null,
        rows: rows.value.filter((row) => row.optionItemId === null),
      },
    ];
    for (const size of sizes.value)
      list.push({
        key: `s${size.id}`,
        title: size.title,
        size: { optionId: size.optionId, optionItemId: size.id },
        rows: rows.value.filter((row) => row.optionItemId === size.id),
      });
    return list;
  });

  const hasNewSizes = computed(() => {
    const price = editor.form.value?.price;
    return price?.mode === 'sizes' && price.rows.some((row) => row.id === null);
  });
  const hasOptionGroups = computed(
    () => !!editor.form.value?.optionLinks.some((link) => link.active)
  );

  const invalidKeys = computed(() => invalidStockRows(rows.value));
  // Stale edits block «Guardar» (a dirty extra that is invalid).
  const invalid = computed(() => invalidKeys.value.length > 0 || stale.value);
  const disabled = busy;

  watch(stale, (value) => emit('stale', value));

  //#region Load and save

  /** «Actualizar»: the lines as saved now, in place of the edited ones. */
  async function reload() {
    const loaded = await reloadLines();
    if (!loaded && !disposed)
      ui.showToast(AlertType.Error, t('menuPage.stock.loadError'));
  }

  /** The editor's step 4. Inputs are disabled while it runs. */
  async function save(itemId: number) {
    await beforeSave();
    const restaurantId = data.restaurant.value?.id ?? null;
    const branchId = loadedBranchId.value;
    if (!base.value || restaurantId === null || branchId === null) return;
    const update = stockMassUpdate(base.value, rows.value, {
      restaurantId,
      branchId,
      itemId,
    });
    // The product save may have removed a size.
    const liveIds = new Set(sizes.value.map((size) => size.id));
    update.toAdd = update.toAdd.filter(
      (recipe) =>
        recipe.optionItemId === undefined || liveIds.has(recipe.optionItemId)
    );
    if (!isEmptyStockUpdate(update))
      await StockRecipesService.massUpdate({
        restaurantId,
        branchId,
        ...update,
      });
    if (!disposed) await load();
  }

  let unregister: (() => void) | null = null;
  onMounted(() => {
    void load();
    unregister = editor.registerExtra({
      key: 'stock',
      dirty,
      invalid,
      save,
      reset,
    });
  });

  onBeforeUnmount(() => {
    disposed = true;
    unregister?.();
  });

  //#endregion

  //#region Rows

  function setQuantity(key: string, quantityText: string) {
    rows.value = rows.value.map((row) =>
      row.key === key ? { ...row, quantityText } : row
    );
  }

  function removeRow(key: string) {
    rows.value = rows.value.filter((row) => row.key !== key);
  }

  function addSupply(block: IStockBlock) {
    modals.show(
      ModalsStockItems,
      {},
      {
        name: STOCK_ITEMS_MODAL,
        adaptive: true,
        classes: 'dialog--height-limit',
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 600,
        clickToClose: true,
      },
      {
        chooseItem: (stockItem: IStockItem) => {
          modals.hide(STOCK_ITEMS_MODAL);
          if (disposed) return;
          const optionItemId = block.size?.optionItemId ?? null;
          if (hasStockRow(rows.value, stockItem.id, optionItemId)) {
            ui.showToast(AlertType.Error, t('menuPage.stock.duplicate'));
            return;
          }
          rows.value = [...rows.value, newStockRow(stockItem, block.size)];
        },
      }
    );
  }

  function rowName(row: IStockRow): string {
    return row.stockItem?.name ?? t('menuPage.stock.unknownSupply');
  }

  //#endregion
</script>

<template>
  <div class="v2-catalog-stock-settings">
    <V2CatalogEditorBanner
      v-if="stale"
      class="stock-stale"
      tone="amber"
      :text="t('assistant.editorStock.changed')"
      :action-text="t('assistant.editorStock.refresh')"
      :action-disabled="disabled"
      @action="reload"
    />
    <p v-if="branchName" class="stock-note">
      {{ t('menuPage.stock.branch', { branch: branchName }) }}
    </p>

    <div v-if="loadFailed" class="stock-failed">
      <p class="stock-note">{{ t('menuPage.stock.loadError') }}</p>
      <PosUiButton color="neutral" variant="outline" @click="load">{{
        t('menuPage.retry')
      }}</PosUiButton>
    </div>

    <div v-else-if="!base" class="stock-loading">
      <PosUiSkeleton class="loading-row" />
      <PosUiSkeleton class="loading-row" />
    </div>

    <template v-else>
      <section v-for="block in blocks" :key="block.key" class="stock-block">
        <h4 v-if="block.title" class="block-title">{{ block.title }}</h4>
        <p v-if="!block.rows.length" class="stock-note">
          {{ t('menuPage.stock.empty') }}
        </p>
        <div v-for="row in block.rows" :key="row.key" class="stock-row">
          <span class="row-name" :title="rowName(row)">{{ rowName(row) }}</span>
          <PosUiInput
            class="row-quantity"
            inputmode="decimal"
            :value="row.quantityText"
            :disabled="disabled"
            :aria-label="t('menuPage.stock.quantityOf', { name: rowName(row) })"
            @input="setQuantity(row.key, $event)"
          />
          <span class="row-unit">{{
            row.stockItem ? row.stockItem.unit : ''
          }}</span>
          <button
            type="button"
            class="row-remove"
            :disabled="disabled"
            :aria-label="t('menuPage.stock.remove', { name: rowName(row) })"
            @click="removeRow(row.key)"
          >
            <i class="mdi mdi-close" aria-hidden="true" />
          </button>
          <p
            v-if="editor.submitted.value && invalidKeys.includes(row.key)"
            class="row-error"
            role="alert"
          >
            {{ t('menuPage.stock.invalidQuantity') }}
          </p>
        </div>
        <PosUiButton
          class="block-add"
          color="neutral"
          variant="ghost"
          icon="plus"
          :disabled="disabled"
          @click="addSupply(block)"
          >{{ t('menuPage.stock.add') }}</PosUiButton
        >
      </section>

      <p v-if="hasNewSizes" class="stock-note">
        {{ t('menuPage.stock.newSizes') }}
      </p>
      <p v-if="hasOptionGroups" class="stock-note">
        {{ t('menuPage.stock.optionsNote') }}
      </p>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-stock-settings {
    .stock-stale {
      @apply tw-mb-2 tw-mt-1;
    }

    .stock-note {
      @apply tw-m-0 tw-mt-1 tw-text-sm tw-leading-relaxed tw-text-neutral-500;
    }

    .stock-failed .stock-note {
      @apply tw-mb-2;
    }

    .stock-loading .loading-row {
      @apply tw-mt-2 tw-h-11 tw-w-full;
    }

    .stock-block {
      @apply tw-mt-3;

      & + .stock-block {
        @apply tw-border-0 tw-border-t tw-border-solid tw-border-neutral-100 tw-pt-3;
      }

      .block-title {
        @apply tw-m-0 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-neutral-500;
      }

      .block-add {
        @apply tw-mt-1;
      }
    }

    .stock-row {
      @apply tw-mt-2 tw-grid tw-items-center tw-gap-x-2;
      grid-template-columns: minmax(0, 1fr) 88px 40px 44px;

      .row-name {
        @apply tw-truncate tw-text-sm tw-text-neutral-900;
      }

      .row-unit {
        @apply tw-text-sm tw-text-neutral-500;
      }

      .row-remove {
        @apply tw-flex tw-h-11 tw-w-11 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-text-lg tw-text-neutral-500;

        &:hover {
          @apply tw-bg-neutral-100 tw-text-neutral-800;
        }

        &:focus-visible {
          @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
        }

        &:disabled {
          @apply tw-cursor-not-allowed tw-opacity-50;
        }
      }

      .row-error {
        @apply tw-col-span-4 tw-m-0 tw-text-xs tw-text-red-600;
      }
    }
  }
</style>
